import { flash, Modal, SubmitButton } from "@xola/ui-kit";
import { omit } from "lodash";
import React from "react";
import { useMutation } from "react-query";
import { Guest, Purchase, Source, Timeslot, Waitlist, WaitlistItem } from "types";
import { api } from "../../../../api/api";
import { useSeller } from "../../../../hooks/useAccount";
import { useResource } from "../../../../hooks/useResource";
import { SecondaryButton } from "../../../Common/SecondaryButton";
import { PurchaseSummaryTile } from "../../../PurchaseModification/PurchaseSummaryTile";

interface ConfirmWaitlistModalProps {
    isOpen: boolean;
    onClose: (confirmedPurchase?: Purchase) => void;
    item: WaitlistItem;
    timeslot: Timeslot;
}

export const ConfirmWaitlistModal = ({ item, isOpen, onClose, timeslot }: ConfirmWaitlistModalProps) => {
    return (
        <Modal isOpen={isOpen} onClose={() => onClose()}>
            <ModalContent item={item} timeslot={timeslot} onClose={onClose} />
        </Modal>
    );
};

const ModalContent = ({ item, onClose, timeslot }: Omit<ConfirmWaitlistModalProps, "isOpen">) => {
    const { data: waitlist } = useResource<Waitlist>(["waitlists", item.purchase.id]);
    const seller = useSeller();

    const { mutate: confirmWaitlist, isLoading } = useMutation(async () => {
        const toCreatePurchase = getPayload(waitlist, timeslot);
        const { data: newPurchase } = await api.post<Purchase>(`/purchases?seller=${seller.id}`, toCreatePurchase);
        flash.success("New Purchase created");
        onClose(newPurchase);
    });

    return (
        <>
            <Modal.Header description="Are you sure you want to confirm this booking?">Confirm Waitlist</Modal.Header>

            <Modal.Body>
                <PurchaseSummaryTile purchase={item.purchase} itemId={item.id} />
            </Modal.Body>

            <Modal.Footer>
                <fieldset className="flex justify-end space-x-4" disabled={isLoading}>
                    <SecondaryButton onClick={() => onClose()}>Cancel</SecondaryButton>

                    <SubmitButton isLoading={isLoading} onClick={() => confirmWaitlist()}>
                        Confirm
                    </SubmitButton>
                </fieldset>
            </Modal.Footer>
        </>
    );
};

const removeIds = (data: any) => {
    return data.map((row: any) => omit(row, ["id"]));
};

export const getPayload = (values: any, timeslot: Timeslot) => {
    const items = values.items.map((item: any) => ({
        ...item,
        arrivalTime: timeslot.time,
        guests: removeIds(item.guests).map((guest: Guest) => omit(guest, ["checkInCount", "ticketCode"])),
        lineItems: removeIds(item.lineItems),
    }));

    return {
        customerName: values.customerName,
        customerEmail: values.customerName,
        phone: values.phone,
        lineItems: [],
        items: removeIds(items),
        seller: values.seller,
        currency: values.currency,
        amount: values.amount,
        deposit: values.deposit,
        notes: values.notes,
        splitPayment: values.splitPayment,
        paymentReminders: values.paymentReminders,
        tags: values.tags,
        organizer: values.organizer,
        waitlist: {
            id: values.id,
        },
        source: Source.OFFICE,
    };
};
